// React hooks over the local store. Live: any local write or merged server change re-renders the readers.
import { useEffect, useState, useSyncExternalStore } from 'react';
import Dexie, { liveQuery } from 'dexie';
import { useSession } from '@/lib/session';
import { openStore, store, type LocalEntry } from './db';
import { getSyncState, subscribeSyncState, watchBaby, type SyncState } from './sync';

/** Opens this user's store; returns false until a user is signed in. */
export function useStoreOpen(): boolean {
  const { me } = useSession();
  if (me) openStore(me.id);
  return !!me;
}

/** Re-runs `query` whenever the tables it reads change. */
export function useLiveQuery<T>(query: () => Promise<T>, deps: unknown[], initial: T): T {
  const [value, setValue] = useState<T>(initial);
  useEffect(() => {
    const sub = liveQuery(query).subscribe({ next: setValue, error: (e) => console.error('[track store]', e) });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return value;
}

/** Keep the baby synced while mounted. */
export function useBabySync(babyId: string | undefined) {
  const open = useStoreOpen();
  useEffect(() => (open && babyId ? watchBaby(babyId) : undefined), [open, babyId]);
}

export type Range = { from: number; to: number };

const MAX_ENTRY_MS = 26 * 3600_000; // longest entry the server accepts (24 h) plus slack
const TIMER_TYPES = new Set<string>(['sleep', 'breast', 'bottle', 'pump']);

/**
 * Entries of a baby overlapping [from, to) (ms), oldest first, deleted ones left out.
 * `null` while the store is opening. Running timers that started inside the window are included.
 */
export function useEntries(babyId: string | undefined, range: Range): LocalEntry[] | null {
  const open = useStoreOpen();
  useBabySync(babyId);
  return useLiveQuery<LocalEntry[] | null>(
    async () => {
      if (!open || !babyId) return null;
      const lo = new Date(range.from - MAX_ENTRY_MS).toISOString();
      const hi = new Date(range.to).toISOString();
      const rows = await store().entries.where('[babyId+startAt]').between([babyId, lo], [babyId, hi], true, false).toArray();
      return rows.filter((e) => {
        if (e.deletedAt) return false;
        if (Date.parse(e.startAt) >= range.from) return true;
        return e.endAt === null ? TIMER_TYPES.has(e.type) : Date.parse(e.endAt) > range.from;
      });
    },
    [open, babyId, range.from, range.to],
    null,
  );
}

/** Entries with no end yet (running timers, plus point-in-time types, which callers filter out). Indexed read. */
export function useOpenEntries(babyId: string | undefined): LocalEntry[] {
  const open = useStoreOpen();
  useBabySync(babyId);
  return useLiveQuery<LocalEntry[]>(
    async () => (open && babyId ? store().entries.where('[babyId+run]').equals([babyId, 1]).toArray() : []),
    [open, babyId],
    [],
  );
}

/** The latest `limit` entries of one type within the last `lookbackDays`, newest first. */
export function useLatest(babyId: string | undefined, type: LocalEntry['type'], limit = 1, lookbackDays = 60): LocalEntry[] {
  const open = useStoreOpen();
  return useLiveQuery<LocalEntry[]>(
    async () => {
      if (!open || !babyId) return [];
      // one indexed range read, filtered here (cursor-by-cursor filtering is slow in some WebKit builds)
      const lo = new Date(Date.now() - lookbackDays * 86_400_000).toISOString();
      const rows = await store().entries.where('[babyId+startAt]').between([babyId, lo], [babyId, Dexie.maxKey]).toArray();
      return rows.filter((e) => e.type === type && !e.deletedAt).sort((a, b) => b.startAt.localeCompare(a.startAt)).slice(0, limit);
    },
    [open, babyId, type, limit, lookbackDays],
    [],
  );
}

/** Start time (ms) of the newest entry of any type, null when there is none (undefined while loading). */
export function useLastEntryAt(babyId: string | undefined): number | null | undefined {
  const open = useStoreOpen();
  return useLiveQuery<number | null | undefined>(
    async () => {
      if (!open || !babyId) return undefined;
      const rows = await store().entries.where('[babyId+startAt]').between([babyId, Dexie.minKey], [babyId, Dexie.maxKey]).reverse().limit(20).toArray();
      const last = rows.find((e) => !e.deletedAt);
      return last ? Date.parse(last.startAt) : null;
    },
    [open, babyId],
    undefined,
  );
}

export function useEntry(id: string | null): LocalEntry | null | undefined {
  const open = useStoreOpen();
  return useLiveQuery<LocalEntry | null | undefined>(async () => (open && id ? (await store().entries.get(id)) ?? null : undefined), [open, id], undefined);
}

/** Queued local changes for a baby (or all babies). */
export function usePendingCount(babyId?: string): number {
  const open = useStoreOpen();
  return useLiveQuery<number>(
    async () => (!open ? 0 : babyId ? store().queue.where('babyId').equals(babyId).count() : store().queue.count()),
    [open, babyId],
    0,
  );
}

export function useSyncState(): SyncState {
  return useSyncExternalStore(subscribeSyncState, getSyncState);
}
